import { DomainEvent } from './DomainEvent';

export type DomainEventHandler<T extends DomainEvent = DomainEvent> = (
    event: T
) => void | Promise<void>;

export interface DomainEventSubscription {
    unsubscribe(): void;
}

/**
 * Domain Event Bus following the Observer pattern.
 * Enables loose coupling between aggregates and cross-cutting concerns.
 */
export interface IDomainEventBus {
    /**
     * Publish a domain event to all registered handlers asynchronously.
     * Use for business operations that may have async handlers.
     */
    publish(event: DomainEvent): Promise<void>;

    /**
     * Publish a domain event to all registered handlers synchronously.
     * Use for high-frequency, real-time events (e.g., windowing, pan, zoom)
     * where performance is critical as it's faster than async publish.
     */
    publishSync(event: DomainEvent): void;

    /**
     * Subscribe to a specific event type
     */
    subscribe<T extends DomainEvent>(
        eventType: string,
        handler: DomainEventHandler<T>
    ): DomainEventSubscription;

    /**
     * Subscribe to all events regardless of type.
     * Use cases:
     * - Logging/debugging all domain events
     * - Analytics and auditing
     * - Event replay systems
     * - Dev tools and inspection
     *
     * For business logic, prefer typed `subscribe()` instead.
     */
    subscribeToAll(handler: DomainEventHandler): DomainEventSubscription;

    /**
     * Clear all subscriptions
     */
    clear(): void;
}

export class InMemoryDomainEventBus implements IDomainEventBus {
    private handlers = new Map<string, Set<DomainEventHandler>>();
    private allEventHandlers = new Set<DomainEventHandler>();

    /**
     * Handlers execute in registration order, making it easy to step through when debugging.
     */
    async publish(event: DomainEvent): Promise<void> {
        // Execute type-specific handlers sequentially
        const typeHandlers = this.handlers.get(event.eventType);
        if (typeHandlers) {
            const promises: Promise<void>[] = [];
            for (const handler of typeHandlers) {
                promises.push(Promise.resolve(handler(event)));
            }
            await Promise.all(promises);
        }

        // Notify handlers subscribed to all events
        if (this.allEventHandlers.size > 0) {
            const promises: Promise<void>[] = [];
            for (const handler of this.allEventHandlers) {
                promises.push(Promise.resolve(handler(event)));
            }
            await Promise.all(promises);
        }
    }

    publishSync(event: DomainEvent): void {
        // Notify handlers for this specific event type
        const typeHandlers = this.handlers.get(event.eventType);
        if (typeHandlers) {
            for (const handler of typeHandlers) {
                handler(event);
            }
        }

        // Notify handlers subscribed to all events
        for (const handler of this.allEventHandlers) {
            handler(event);
        }
    }

    subscribe<T extends DomainEvent>(
        eventType: string,
        handler: DomainEventHandler<T>
    ): DomainEventSubscription {
        if (!this.handlers.has(eventType)) {
            this.handlers.set(eventType, new Set());
        }

        this.handlers.get(eventType)!.add(handler as DomainEventHandler);

        return {
            unsubscribe: () => {
                const handlers = this.handlers.get(eventType);
                if (handlers) {
                    handlers.delete(handler as DomainEventHandler);
                    if (handlers.size === 0) {
                        this.handlers.delete(eventType);
                    }
                }
            },
        };
    }

    subscribeToAll(handler: DomainEventHandler): DomainEventSubscription {
        this.allEventHandlers.add(handler);

        return {
            unsubscribe: () => {
                this.allEventHandlers.delete(handler);
            },
        };
    }

    clear(): void {
        this.handlers.clear();
        this.allEventHandlers.clear();
    }
}

/**
 * Global domain event bus instance.
 */
export const domainEventBus = new InMemoryDomainEventBus();
